// STUCK PRs — the read model behind "which AI-managed PRs are stuck, and why"
// (#6338 wave 1). The dashboard, the API's escalation queue and the MCP `status`
// block all ask that one question; this module is the ONE answer, replacing the
// narrower upcoming-hand-over projection (`./agent-queue-upcoming.ts`, deleted in
// wave 2). The CANDIDATES are read in `@developerz/db` (`queries/stuck-prs.ts`)
// on facts only — open PR, enabled repo, AI-managed author, idle past the floor —
// and never on a reason or an action, so a park the classifier has not met yet
// still shows up as a row instead of vanishing.
//
// WHO DECIDES WHAT. "AI-managed" is decided HERE, by `aiManagedPr` itself over the
// stored author and the repo's RESOLVED auto-merge — the candidate carries the
// resolved boolean because this package may not import `@developerz/policy`; the
// caller resolves it through `resolvedAutoMerge` the way the babysit lane does,
// so the read cannot disagree with the rule on who counts. "Stuck ON what" is
// `parkOf`'s ordered ladder below; the escalation-reason rung of that ladder
// classifies through `entities/babysit/park-class.ts`, whose tables are TOTAL
// over the reason vocabulary both directions. A reason the tables do not know
// parks as `unclassified` with the stored value passed through — this module
// never throws on a stored value, because a read that dies on drift hides every
// OTHER row behind the same call.
//
// `collapsed` delivery is passed through AS STORED: that row itself mailed
// nobody (another copy went — the vocabulary's own table,
// `entities/escalation.ts`). The label is the caller's render.

import type { ParkClass, SweepsLadder } from '../entities/babysit';
import {
  aiManagedPr,
  isCiRerunRescueReason,
  isReviewRescueReason,
  PARK_CLASS,
  parkClassOf,
  STUCK_PR_RESCUE_AFTER_MS,
} from '../entities/babysit';
import type { EscalationDeliveryState } from '../entities/escalation';
import { TaskStatus } from '../entities/task';
import type { PolicySnapshotPostureRead } from './merge-hold';
import { STUCK_PR_RESCUE_MINT_REASONS } from './task-envelope';

/** The statuses a follow-up row is IN FLIGHT under — the rescue's own live set. */
const IN_FLIGHT_FOLLOWUP_STATUSES: readonly string[] = [
  TaskStatus.Pending,
  TaskStatus.Assigned,
  TaskStatus.InProgress,
];

/**
 * How idle a stuck PR may sit before the alert names it. Exported for the fleet
 * alarm's evaluator (#6340), which grades each leg on its own key — the ONE
 * spelling of the number, so `alertAt` and the alarm cannot drift.
 */
export const STUCK_PR_ALERT_AFTER_IDLE_MS = 6 * 60 * 60_000;

/**
 * How long a stuck PR may be OPEN, however fresh its head, before the alert names it:
 * 12h, the merge target itself (owner ruling ivndev001 2026-11-03; idle was 24h, open 72h).
 */
export const STUCK_PR_ALERT_AFTER_OPEN_MS = 12 * 60 * 60_000;

/** The newest babysit hop of the PR, as the read answers it — `null` when there is none. */
export type StuckPrHop = {
  /** The hop's executed arm (`BabysitExecuted`), as stored. */
  readonly executed: string;
  /** The decision's action kind (`input.action`), as stored. */
  readonly action: string | null;
  /** The driver's reported state (`output.state`), as stored. */
  readonly state: string | null;
  readonly at: Date;
  /** The head the hop observed — the PR's current head when it still matches. */
  readonly headSha: string | null;
};

/** The escalation the newest hop landed on, as stored. */
export type StuckPrEscalation = {
  readonly id: string;
  /** `payload.babysit_reason` — the structured discriminant, possibly unknown to the tables. */
  readonly reason: string;
  readonly deliveryState: EscalationDeliveryState;
  readonly createdAt: Date;
  /**
   * `input.ladder` of the hop that named this row, as stored: on a `max_sweeps_exceeded`
   * escalation, which ladder's ceiling ran out (`SweepsLadder`, #6231). `null` on every other
   * reason, and on a hop recorded before the hop carried it.
   */
  readonly ladder: string | null;
};

/** The one in-flight `pr_babysit` follow-up of the PR, when there is one. */
export type StuckPrFollowup = {
  readonly id: string;
  /** A task status, as stored — the column is text and this read does not parse it. */
  readonly status: string;
  readonly holdReason: string | null;
  readonly createdAt: Date;
};

/** The newest rescue-marked follow-up since the re-arm, when there is one. */
export type StuckPrRescueMark = {
  readonly reason: string;
  readonly status: string;
  readonly failureKind: string | null;
};

/**
 * One open PR as the db read answers it. Every stored value passes through
 * unparsed — the park ladder, not a schema, grades it.
 */
export type StuckPrCandidate = {
  readonly repoFullName: string;
  readonly prNumber: number;
  /** The account read always has one (`pull_requests.title` is NOT NULL); the fleet read nulls it. */
  readonly title: string | null;
  readonly authorLogin: string;
  readonly openedAt: Date | null;
  /** `pull_requests.head_sha` — `null` when the projection has not written one. */
  readonly headSha: string | null;
  /** `pull_requests.babysit_rearmed_at` — `null` = the whole history. */
  readonly rearmedAt: Date | null;
  /** The human's merge stamp, as stored (`auto`, `ask`, `block`). */
  readonly mergeDecision: string | null;
  /** `pull_requests.merge_decision_head_sha` — the head an `auto` stamp approved. */
  readonly mergeDecisionHeadSha: string | null;
  /** `blocked` | `behind` | `draft`, as stored. */
  readonly mergeBlockState: string | null;
  /**
   * The repo's auto-merge as the babysit lane resolves it — `resolvedAutoMerge`
   * (`@developerz/policy`), resolved by the CALLER: this package cannot run the
   * tier gate, and a read that guessed `parsed->'pr'->>'auto_merge'` would call
   * every stored row auto-merging (the schema default is `true`).
   */
  readonly autoMerge: boolean;
  /**
   * The repo's ACTIVE valid snapshot (`null` when it has none), for a caller that must
   * re-resolve, and the platform-side merge hold beside it, read off the repo row in the
   * same query (#7011) so a held repo with no snapshot still carries its hold, and the
   * account's posture (plan 102 slice 06), which a policy-less repo resolves its default by.
   */
  readonly policy: PolicySnapshotPostureRead;
  readonly newestHop: StuckPrHop | null;
  /**
   * The first hop on the head the read treats as current (the PR's `head_sha`,
   * else the newest hop's) — `null` when no hop observed that head.
   */
  readonly headFirstSeenAt: Date | null;
  readonly escalation: StuckPrEscalation | null;
  readonly inflightFollowup: StuckPrFollowup | null;
  readonly rescueMarked: StuckPrRescueMark | null;
};

/** What a stuck PR is parked ON. The wire's `park_class`. */
export type StuckPrParkClass =
  | ParkClass
  | 'unobserved'
  | 'unobserved_head'
  | 'waiting'
  | 'unclassified';

/**
 * Every {@link StuckPrParkClass}, for a wire enum that stays total over the union —
 * `PARK_CLASS`'s values spelled out beside the four the park ladder adds, so a class
 * renamed in `entities/babysit/park-class.ts` or a rung added here fails typecheck on
 * this list rather than shipping a wire that refuses rows it just learned to classify.
 */
export const STUCK_PR_PARK_CLASSES = [
  'coding',
  'review',
  'in_flight',
  'ci_infra',
  'human_hold',
  'external_hold',
  'github_only',
  'repo_config',
  'platform_fault',
  'not_ai_managed',
  'gate_edit_owner_applies',
  'unobserved',
  'unobserved_head',
  'waiting',
  'unclassified',
] as const satisfies readonly [StuckPrParkClass, ...StuckPrParkClass[]];
/** The reverse check `satisfies` cannot make: a member of the union this list omits. */
const NO_STALE_PARK_CLASS_LIST: Exclude<
  StuckPrParkClass,
  (typeof STUCK_PR_PARK_CLASSES)[number]
> extends never
  ? true
  : never = true;
void NO_STALE_PARK_CLASS_LIST;

/** The park ladder's answer for one PR: the class, the reason it cites, a blocker token. */
export type StuckPrPark = {
  readonly parkClass: StuckPrParkClass;
  /** The stored escalation reason the class cites, when it cites one. */
  readonly reason: string | null;
  /**
   * A machine-readable token naming the blocker (`escalation:<reason>`,
   * `merge_decision:<stamp>`, `merge_block:<state>`, `followup:<hold|status>`, …),
   * or `null` when nothing names it. Consumers branch on the class, never parse this.
   */
  readonly blocker: string | null;
};

/** Which clock `idleSince` is — the wire's `idle_basis`. */
export type StuckPrIdleBasis = 'head_first_seen' | 'unobserved_head' | 'unobserved';

/** What the rescue leg of the read says about one PR. */
export type StuckPrRescueState =
  | { readonly kind: 'none' }
  | { readonly kind: 'live'; readonly reason: string }
  | { readonly kind: 'spent'; readonly reason: string; readonly outcome: string };

/** One stuck PR, classified. The row the API and the MCP block serve. */
export type StuckPrRow = {
  readonly repoFullName: string;
  readonly prNumber: number;
  readonly title: string | null;
  readonly authorLogin: string;
  readonly park: StuckPrPark;
  readonly idleBasis: StuckPrIdleBasis;
  readonly idleSince: Date | null;
  readonly openedAt: Date | null;
  /** What the newest hop DID, and when — `null` when the PR was never observed. */
  readonly lastAction: { readonly executed: string; readonly at: Date } | null;
  readonly rescue: StuckPrRescueState;
  readonly escalation: StuckPrEscalation | null;
  /**
   * When the rescue's hand-over clock runs out — `idleSince` plus
   * {@link STUCK_PR_RESCUE_AFTER_MS} — set ONLY where no later rung of the park
   * ladder explains the wait instead of the escalation (a human's merge stamp, a
   * live round) AND for a reason some rescue answers ({@link isRescuableReason}): those
   * rows wait on what parks them, and a reason no rescue answers has no hand-over at
   * the clock — either way a due instant would promise one that cannot happen. `null`
   * otherwise (and when `idleSince` itself is `null`).
   */
  readonly rescueDueAt: Date | null;
  /** The earlier of 6h idle and 12h open — the alert's clock. */
  readonly alertAt: Date | null;
};

/** The rescue's anchor rule: a re-arm NEWER than a hop restarts that clock at the re-arm. */
function anchoredAfterRearm(at: Date, rearmedAt: Date | null): Date {
  return rearmedAt !== null && rearmedAt > at ? rearmedAt : at;
}

/**
 * The `max_sweeps_exceeded` ladders a rescue buys a round of (`rescueStuckPr`): `comments`
 * stands, since a person is in that loop, and an untagged row names no round at all (#6240).
 */
const RESCUED_SWEEPS_LADDERS: readonly SweepsLadder[] = ['ci', 'findings', 'conflict'];

/**
 * Is this stored escalation one a rescue answers at the rescue clock? Three sets, one per spender:
 * `STUCK_PR_RESCUE_MINT_REASONS` (a rescue round, `rescueStuckPr`), `CI_RERUN_RESCUE_REASONS`
 * (one cancel-then-rerun `rerun_checks` hop, #6346, also `rescueStuckPr`) and
 * `REVIEW_RESCUE_REASONS` (one fresh re-review the worker spends, #6345,
 * `reviewRescueReason` in `apps/worker`). Reading the mint set alone left a due CI re-run
 * or re-review with no due instant on the row.
 */
function isRescuableReason({ reason, ladder }: StuckPrEscalation): boolean {
  if (reason === 'max_sweeps_exceeded') {
    return (RESCUED_SWEEPS_LADDERS as readonly (string | null)[]).includes(ladder);
  }
  return (
    (STUCK_PR_RESCUE_MINT_REASONS as readonly string[]).includes(reason) ||
    isCiRerunRescueReason(reason) ||
    isReviewRescueReason(reason)
  );
}

/** The earlier of two nullable instants — `null` when both are. */
function earlierOf(a: Date | null, b: Date | null): Date | null {
  if (a === null) return b;
  if (b === null) return a;
  return a <= b ? a : b;
}

/**
 * The human's stamp when it HOLDS this PR, or `null` when it passes — graded the way the
 * merge egress grades it (`mergeDecisionPasses`, `packages/review/src/merge-gate/gate.ts`,
 * which this package may not import): no stamp passes, and `auto` passes only on the
 * head it approved. An `auto` on another head reads `auto_stale`; `ask`, `block` and any
 * unrecognized value hold as stored, so an unknown stamp never reads as permissive.
 */
function mergeDecisionHoldOf(c: StuckPrCandidate): string | null {
  if (c.mergeDecision === null) return null;
  if (c.mergeDecision !== 'auto') return c.mergeDecision;
  const head = c.headSha ?? c.newestHop?.headSha ?? null;
  return head !== null && c.mergeDecisionHeadSha === head ? null : 'auto_stale';
}

/**
 * What the PR is stuck on — the ordered ladder, FIRST match wins:
 *
 *   unobserved → unobserved_head → the human's merge stamp → a live round →
 *   the escalation reason → a merge refusal → waiting
 *
 * Each rung names something the row itself carries, in that order because each
 * earlier rung makes the later ones unanswerable: nobody can say what a PR is
 * stuck on before anyone has observed it, and a human's stamp or a running round
 * explains the wait better than the condition that filed it. Never throws: an
 * escalation reason the park tables do not know parks as `unclassified` with the
 * stored value in `reason`. Pure.
 */
export function parkOf(c: StuckPrCandidate): StuckPrPark {
  if (c.newestHop === null) {
    return { parkClass: 'unobserved', reason: null, blocker: 'no_babysit_hops' };
  }
  if (c.headFirstSeenAt === null) {
    return { parkClass: 'unobserved_head', reason: null, blocker: 'head_never_observed' };
  }
  const decisionHold = mergeDecisionHoldOf(c);
  if (decisionHold !== null) {
    return {
      parkClass: PARK_CLASS.merge_decision,
      reason: 'merge_decision',
      blocker: `merge_decision:${decisionHold}`,
    };
  }
  if (c.inflightFollowup !== null) {
    return {
      parkClass: PARK_CLASS.followup_held,
      reason: null,
      blocker: `followup:${c.inflightFollowup.holdReason ?? c.inflightFollowup.status}`,
    };
  }
  if (c.escalation !== null) {
    return {
      parkClass: parkClassOf(c.escalation.reason) ?? 'unclassified',
      reason: c.escalation.reason,
      blocker: `escalation:${c.escalation.reason}`,
    };
  }
  if (c.mergeBlockState !== null) {
    return { parkClass: 'waiting', reason: null, blocker: `merge_block:${c.mergeBlockState}` };
  }
  return { parkClass: 'waiting', reason: null, blocker: null };
}

/** The idle grade — which clock, and where it stands. See {@link StuckPrIdleBasis}. */
function idleOf(c: StuckPrCandidate): { basis: StuckPrIdleBasis; since: Date | null } {
  if (c.newestHop === null) {
    const since =
      c.openedAt === null
        ? c.rearmedAt
        : c.rearmedAt !== null && c.rearmedAt > c.openedAt
          ? c.rearmedAt
          : c.openedAt;
    return { basis: 'unobserved', since };
  }
  if (c.headFirstSeenAt === null) {
    // Hops exist, but none observed the head the read treats as current: the
    // newest hop is the last thing that happened to this PR — unless the re-arm
    // is newer, which restarts this clock like every other (the rescue's anchor rule).
    return {
      basis: 'unobserved_head',
      since: anchoredAfterRearm(c.newestHop.at, c.rearmedAt),
    };
  }
  return { basis: 'head_first_seen', since: anchoredAfterRearm(c.headFirstSeenAt, c.rearmedAt) };
}

/** The rescue leg: a round still running, or the newest spent one, with its outcome. */
function rescueOf(c: StuckPrCandidate): StuckPrRescueState {
  if (c.rescueMarked === null) return { kind: 'none' };
  if (IN_FLIGHT_FOLLOWUP_STATUSES.includes(c.rescueMarked.status)) {
    return { kind: 'live', reason: c.rescueMarked.reason };
  }
  return {
    kind: 'spent',
    reason: c.rescueMarked.reason,
    outcome: c.rescueMarked.failureKind ?? c.rescueMarked.status,
  };
}

/** One candidate, classified into a row. */
function rowOf(c: StuckPrCandidate): StuckPrRow {
  const idle = idleOf(c);
  // The due instant is promised only where no later rung of the park ladder explains
  // the wait INSTEAD of the escalation: a row parked on a human's merge stamp waits on
  // that person and a row with a live round waits on the round, so a due instant on
  // either would promise a hand-over the park will not run. Graded on the rungs' own
  // predicates (`mergeDecisionHoldOf`, `inflightFollowup`), never by re-parsing the
  // park — an observation rung (`unobserved_head`) still carries the escalation's clock.
  const rescueDueAt =
    c.escalation !== null &&
    mergeDecisionHoldOf(c) === null &&
    c.inflightFollowup === null &&
    idle.since !== null &&
    isRescuableReason(c.escalation)
      ? new Date(idle.since.getTime() + STUCK_PR_RESCUE_AFTER_MS)
      : null;
  const alertAt = earlierOf(
    idle.since === null ? null : new Date(idle.since.getTime() + STUCK_PR_ALERT_AFTER_IDLE_MS),
    c.openedAt === null ? null : new Date(c.openedAt.getTime() + STUCK_PR_ALERT_AFTER_OPEN_MS),
  );
  return {
    repoFullName: c.repoFullName,
    prNumber: c.prNumber,
    title: c.title,
    authorLogin: c.authorLogin,
    park: parkOf(c),
    idleBasis: idle.basis,
    idleSince: idle.since,
    openedAt: c.openedAt,
    lastAction:
      c.newestHop === null ? null : { executed: c.newestHop.executed, at: c.newestHop.at },
    rescue: rescueOf(c),
    escalation: c.escalation,
    rescueDueAt,
    alertAt,
  };
}

/**
 * The account's stuck AI-managed PRs, longest idle first — see
 * the module header for what "stuck" is built on. `ourBotLogin` is
 * `requireOurBotLogin()`. Every clock on the row is an absolute instant, so a
 * relative question ("due now?") stays the caller's render on the caller's
 * clock. Only candidates `aiManagedPr` admits are kept; a row whose idle clock
 * cannot be computed at all (no hops, no `opened_at`, no re-arm — impossible via
 * the db read, which floors on one of them) sorts LAST, visible, never dropped.
 * Pure.
 */
export function stuckPrsOf(
  candidates: readonly StuckPrCandidate[],
  input: { ourBotLogin: string },
): StuckPrRow[] {
  const ours = input.ourBotLogin.toLowerCase();
  return candidates
    .filter((c) =>
      aiManagedPr(
        { author: { login: c.authorLogin, isOurs: c.authorLogin.toLowerCase() === ours } },
        { autoMerge: c.autoMerge },
      ),
    )
    .map(rowOf)
    .sort((a, b) => {
      if (a.idleSince === null && b.idleSince === null) {
        return a.repoFullName === b.repoFullName
          ? a.prNumber - b.prNumber
          : a.repoFullName < b.repoFullName
            ? -1
            : 1;
      }
      if (a.idleSince === null) return 1;
      if (b.idleSince === null) return -1;
      if (a.idleSince.getTime() !== b.idleSince.getTime()) {
        return a.idleSince.getTime() - b.idleSince.getTime();
      }
      return a.repoFullName === b.repoFullName
        ? a.prNumber - b.prNumber
        : a.repoFullName < b.repoFullName
          ? -1
          : 1;
    });
}
